import React, { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { api, fileUrl } from "../lib/api";
import PlotReveal from "../components/PlotReveal";
import VerticalTag from "../components/VerticalTag";
import WavyEnd from "../components/WavyEnd";

const DISCIPLINES = ["All", "Residential Architecture", "Residential Interiors", "Commercial Architecture", "Commercial Fit-outs"];
const FALLBACK = "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?fm=jpg&q=85&w=2400";

function heroStyle(p) {
  const fx = p?.hero_focal_x ?? 50, fy = p?.hero_focal_y ?? 50;
  const scale = (p?.hero_scale ?? 100) / 100;
  return { objectFit: "cover", objectPosition: `${fx}% ${fy}%`, transform: `scale(${scale})`, transformOrigin: `${fx}% ${fy}%` };
}

export default function Work() {
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("register");
  const active = params.get("discipline") || "All";
  const [hoverIdx, setHoverIdx] = useState(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects", "public"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const filtered = useMemo(() => {
    if (active === "All") return projects;
    return projects.filter((p) => p.discipline === active);
  }, [projects, active]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const hoverProject = hoverIdx != null ? filtered[hoverIdx] : null;
  const hoverImg = hoverProject?.gallery?.find((g) => g.id === hoverProject.hero_image_id) || hoverProject?.gallery?.[0];
  const hoverSrc = hoverImg ? fileUrl(hoverImg.variants.grid) : FALLBACK;

  return (
    <div data-testid="work-page" className="pt-28 md:pt-36 container-w"
      onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}>
      <VerticalTag text="Work — Index / 2020 to Present" />
      {/* Header */}
      <div className="mb-16 md:mb-24 relative">
        <div aria-hidden className="pointer-events-none absolute right-0 top-4 opacity-[0.14] select-none">
          <img src="/brand/typography-greige.png" alt="" className="w-[420px] md:w-[560px]" />
        </div>
        <div className="mono-label mb-4">Index — 2020 to Present</div>
        <h1 className="font-display italic text-5xl md:text-7xl lg:text-9xl leading-[0.9] tracking-tight">Work.</h1>
        <WavyEnd className="text-marine mt-4" width={200} />
      </div>

      {/* Filters + view toggle */}
      <div className="border-y border-ink/15 py-6 mb-16 md:mb-20 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {DISCIPLINES.map((d) => (
            <button
              key={d}
              onClick={() => setParams(d === "All" ? {} : { discipline: d })}
              data-testid={`filter-${d.replace(/\s+/g, "-").toLowerCase()}`}
              className={`mono-label transition-colors ${active === d ? "text-marine" : "text-ash hover:text-ink"}`}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <span className="mono-label">View</span>
          <button data-testid="view-register" onClick={() => setView("register")}
            className={`mono-label ${view === "register" ? "text-marine" : "text-ash hover:text-ink"}`}>Register</button>
          <span className="text-mist">|</span>
          <button data-testid="view-grid" onClick={() => setView("grid")}
            className={`mono-label ${view === "grid" ? "text-marine" : "text-ash hover:text-ink"}`}>Grid</button>
        </div>
      </div>

      {isLoading && <div className="mono-label py-24">Loading…</div>}
      {!isLoading && filtered.length === 0 && (
        <div className="py-24 text-center">
          <div className="mono-label mb-4">No projects yet</div>
          <p className="font-display italic text-3xl">The register is being prepared.</p>
        </div>
      )}

      {/* Register view */}
      {view === "register" && filtered.length > 0 && (
        <div className="relative">
          <ul data-testid="register-list">
            {filtered.map((p, i) => {
              const isHover = hoverIdx === i;
              return (
                <li key={p.id} className="border-b border-ink/15"
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx((cur) => (cur === i ? null : cur))}
                >
                  <Link to={`/work/${p.slug}`} className="grid grid-cols-12 items-center py-7 md:py-10 gap-4" data-testid={`register-row-${i}`}>
                    <div className="col-span-1 mono-label">{String(i + 1).padStart(2, "0")}</div>
                    <div className="col-span-11 md:col-span-5 font-display italic text-3xl md:text-5xl tracking-tight">
                      <motion.span
                        animate={{ x: isHover ? 24 : 0, color: isHover ? "#16403F" : "#0E1113" }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      >
                        {p.title}
                      </motion.span>
                    </div>
                    <div className="hidden md:block md:col-span-3 mono-label">{p.discipline}</div>
                    <div className="hidden md:block md:col-span-2 mono-label">{p.location}</div>
                    <div className="hidden md:block md:col-span-1 mono-label text-right">{p.year}</div>
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Cursor-following peek image (Register Hover) — adapts to the
              hero image's own aspect ratio (landscape / portrait / square)
              so the preview never distorts or over-crops. */}
          <AnimatePresence>
            {hoverProject && (() => {
              const iw = hoverImg?.width || 3;
              const ih = hoverImg?.height || 4;
              const ar = iw / ih;
              // Base the peek on a fixed longest-edge (240px) and adapt the
              // other edge from the image's ratio, with a modest min so ultra-
              // panoramic and ultra-tall images stay usable.
              const LONG = 240;
              const SHORT_MIN = 120;
              let w, h;
              if (ar >= 1) {
                w = LONG;
                h = Math.max(SHORT_MIN, Math.round(LONG / ar));
              } else {
                h = LONG;
                w = Math.max(SHORT_MIN, Math.round(LONG * ar));
              }
              // Frame padding is 8px on each side (p-2 = 0.5rem)
              const framePad = 16;
              return (
              <motion.div
                key={hoverProject.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="hidden md:block pointer-events-none fixed z-30"
                data-testid="register-peek"
                style={{
                  left: cursorPos.x + 28,
                  top: cursorPos.y - Math.round((h + framePad) / 2),
                  width: w + framePad,
                  height: h + framePad,
                }}
              >
                <div
                  className="relative w-full h-full bg-paper p-2"
                  style={{
                    borderRadius: "14px",
                    border: "1px solid rgba(14,17,19,0.28)",
                    boxShadow: "0 12px 32px -14px rgba(14,17,19,0.28)",
                  }}
                >
                  <div className="relative w-full h-full overflow-hidden" style={{ borderRadius: "10px" }}>
                    <img
                      src={hoverSrc}
                      alt={hoverProject.title}
                      className="w-full h-full object-cover"
                      style={heroStyle(hoverProject)}
                    />
                  </div>
                  <div
                    className="absolute inset-x-2 bottom-2 px-2 py-1.5 bg-paper/95 flex items-center justify-between mono-label"
                    style={{ fontSize: "9px", letterSpacing: "0.1em", borderRadius: "6px" }}
                  >
                    <span className="truncate">{hoverProject.title}</span>
                    <span className="text-ash ml-2">{hoverProject.year}</span>
                  </div>
                </div>
              </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      )}

      {/* Grid view */}
      {view === "grid" && filtered.length > 0 && (
        <div data-testid="grid-list" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-28">
          {filtered.map((p, i) => {
            const img = p.gallery?.find((g) => g.id === p.hero_image_id) || p.gallery?.[0];
            const src = img ? fileUrl(img.variants.grid) : FALLBACK;
            return (
              <Link key={p.id} to={`/work/${p.slug}`} className={`block ${i % 3 === 0 ? "md:col-span-2" : ""}`} data-testid={`grid-item-${i}`}>
                <PlotReveal>
                  <div className="overflow-hidden">
                    <motion.img src={src} alt={p.title}
                      className={`w-full ${i % 3 === 0 ? "h-[75vh]" : "h-[540px]"}`}
                      style={heroStyle(p)}
                      whileHover={{ scale: 1.04 }}
                      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                </PlotReveal>
                <div className="mt-3 flex justify-between items-baseline">
                  <span className="font-display italic text-2xl">{p.title}</span>
                  <span className="mono-label">{p.year}</span>
                </div>
                <div className="mono-label mt-1">{p.discipline} · {p.location}</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
